export function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function fmtRel(input?: string | { seconds: number }): string {
  if (!input) return "";
  const ms = typeof input === "string" ? new Date(input).getTime() : input.seconds * 1000;
  const diff = Date.now() - ms;
  const m = Math.floor(diff / 60000);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (m < 2) return "just now";
  if (m < 60) return `${m} min ago`;
  if (h < 24) return `${h}h ago`;
  if (d < 7) return `${d}d ago`;
  const dt = new Date(ms);
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function fmtDateShort(input?: string | { seconds: number }): string {
  if (!input) return "";
  const ms = typeof input === "string" ? new Date(input).getTime() : input.seconds * 1000;
  return new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/* multiply an ingredient base by a serving ratio, keeping a sane display */
export function scaleAmount(base: number | string | undefined, ratio: number): string {
  if (base == null || base === "") return "";
  const n = typeof base === "number" ? base : parseFloat(base);
  if (!Number.isFinite(n)) return String(base);
  const scaled = n * ratio;
  if (scaled >= 10) return scaled.toFixed(0);
  if (Number.isInteger(scaled)) return String(scaled);
  return scaled.toFixed(scaled < 1 ? 2 : 1).replace(/\.?0+$/, "");
}

/* ── Scaling amounts embedded in free-text step instructions ──────────────
 * Step prose mixes quantities ("1 cup rice", "2.3 lb chicken") with things
 * that must NOT scale: cook times ("8 minutes"), temperatures ("350°F"), and
 * cut/cookware sizes ("1-inch chunks", "8-inch skillet"). So we only scale a
 * number that is anchored to a measurement unit (or that leads a bullet line),
 * and we hard-skip anything trailed by a time/temp/length word. Under-scaling
 * (leaving a bare count alone) is the safe failure mode; mangling a cook time
 * is not. */

const UNICODE_FRAC: Record<string, number> = {
  "½": 0.5, "⅓": 1 / 3, "⅔": 2 / 3, "¼": 0.25, "¾": 0.75,
  "⅛": 0.125, "⅜": 0.375, "⅝": 0.625, "⅞": 0.875,
  "⅙": 1 / 6, "⅚": 5 / 6, "⅕": 0.2, "⅖": 0.4, "⅗": 0.6, "⅘": 0.8,
};
const FRAC_CHARS = Object.keys(UNICODE_FRAC).join("");

// Measurement units that mark a number as a scalable quantity.
const SCALE_UNITS = [
  "cups?", "tbsps?", "tablespoons?", "tsps?", "teaspoons?",
  "milliliters?", "millilitres?", "ml", "liters?", "litres?", "l",
  "quarts?", "qt", "pints?", "pt", "gallons?", "gal", "fl",
  "grams?", "g", "kilograms?", "kg", "mg", "ounces?", "oz", "pounds?", "lbs?",
  "cloves?", "cans?", "sticks?", "sprigs?", "stalks?", "bunch(?:es)?",
  "heads?", "slices?", "strips?", "fillets?", "pieces?", "pinch(?:es)?",
].join("|");

// Words that mean "do NOT scale this number" (times, temps, lengths, %).
const STOP_UNITS = [
  "minutes?", "mins?", "hours?", "hrs?", "seconds?", "secs?",
  "degrees?", "inch(?:es)?", "cm", "mm", "days?", "weeks?", "months?",
].join("|");

const QTY_CORE =
  `\\d+\\s+\\d+\\/\\d+` +              // mixed "1 1/2"
  `|\\d+\\/\\d+` +                      // fraction "1/2"
  `|\\d+\\s*[${FRAC_CHARS}]` +          // "1½"
  `|\\d*\\.\\d+` +                      // decimal "2.3", ".5"
  `|\\d+` +                            // integer
  `|[${FRAC_CHARS}]`;                  // bare "½"

/** Parse a quantity token — "2", "1.5", "1/2", "1 1/2", "1½", "½" — to a
 *  number, or null when it isn't a quantity at all ("to taste"). Also used by
 *  the shopping list to total ingredient amounts across recipes. */
export function parseQty(tok: string): number | null {
  const t = tok.trim();
  if (UNICODE_FRAC[t] != null) return UNICODE_FRAC[t];
  let m = t.match(new RegExp(`^(\\d+)\\s*([${FRAC_CHARS}])$`));
  if (m) return +m[1] + UNICODE_FRAC[m[2]];
  m = t.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (m) return +m[1] + +m[2] / +m[3];
  m = t.match(/^(\d+)\/(\d+)$/);
  if (m) return +m[1] / +m[2];
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : null;
}

const NICE_FRACTIONS: Array<[number, string]> = [
  [0.25, "¼"], [1 / 3, "⅓"], [0.5, "½"], [2 / 3, "⅔"], [0.75, "¾"],
];

/** Render a quantity for reading: whole numbers where possible, nice unicode
 *  fractions for the common kitchen thirds/quarters, integers above 10. */
export function fmtQty(n: number): string {
  const rounded = Math.round(n * 100) / 100;
  if (rounded >= 10) return String(Math.round(rounded));
  const whole = Math.floor(rounded + 1e-9);
  const frac = rounded - whole;
  if (frac < 0.04) return String(whole);
  for (const [v, glyph] of NICE_FRACTIONS) {
    if (Math.abs(frac - v) < 0.04) return whole > 0 ? `${whole}${glyph}` : glyph;
  }
  return String(rounded).replace(/\.?0+$/, "");
}

/**
 * Scale measurement amounts inside a free-text instruction string.
 * `leading: true` (for bullet lines, which read "<amount> <ingredient>") also
 * scales a quantity that starts the string even without a trailing unit.
 */
export function scaleText(
  text: string | undefined,
  ratio: number,
  opts: { leading?: boolean } = {}
): string {
  if (!text || ratio === 1) return text ?? "";
  const scan = new RegExp(
    `(${QTY_CORE})((\\s*(?:to|[-–—])\\s*)(${QTY_CORE}))?`,
    "g"
  );
  const stopRe = new RegExp(`^[\\s-]*(?:${STOP_UNITS})\\b`, "i");
  const unitRe = new RegExp(`^\\s+(?:[A-Za-z]+\\s+){0,2}(?:${SCALE_UNITS})\\b`, "i");
  return text.replace(scan, (full, q1, _tail, sep, q2, offset: number, str: string) => {
    const after = str.slice(offset + full.length);
    // Never scale numbers tied to a time / temperature / length.
    if (stopRe.test(after) || /^[\s-]*[°%]/.test(after)) return full;
    const unitAnchored = unitRe.test(after);
    const isLeading = !!opts.leading && /^\s*$/.test(str.slice(0, offset));
    if (!unitAnchored && !isLeading) return full;
    const a = parseQty(q1);
    if (a == null) return full;
    if (sep && q2 != null) {
      const b = parseQty(q2);
      if (b == null) return full;
      return `${fmtQty(a * ratio)}${sep}${fmtQty(b * ratio)}`;
    }
    return fmtQty(a * ratio);
  });
}
